import { useEffect, useState } from 'react';

import { Constants } from '@constants';
import { numbersOnly } from '@forms/helpers/normalisers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft } from '@iwoca/icons';
import { PostApplicationRequestBody } from '@iwoca/lapi-client/edge.js';
import { Button, Card, ErrorMessage, Input, Select } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import _ from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import * as multiLoanselectors from '../../multiLoanDashboard/dashboardWidgets/tabularFlexiDashboard/multiLoanDashboardSelector';
import * as selectors from '../applyFlexiLoanSelectors';
import {
    APPLY_FLEXI_LOAN_SUBMIT_APPLICATION,
    CREATE_APPLICATION
} from '../ApplyFlexiLoanView.actions';
import { applyFlexiLoanFormResolver } from './applyFlexiLoanFormResolver';
import styles from './ApplyFlexiLoanFormView.module.css';
import ApprovalRequirements from './ApprovalRequirements/ApprovalRequirements';
import { loanDurationOptionsDE } from './loanDurationOptionsDE';
import loanPurposeOptions from './loanPurposeOptions.json';
import loanPurposeOptionsDE from './loanPurposeOptionsDE.json';
import { NEW_IwLink } from '../../../iwRouter/NEW_IwLink';

// Returns '' (not undefined/NaN) for empty input: react-hook-form's
// Controller onChange ignores `undefined`, which would leave the previous
// value in place when the user clears the field. An empty string clears it
// and is falsy for the "missing field" validation checks.
const parseCurrencyInput = (raw: string): number | '' => {
    const digits = numbersOnly(raw);
    if (!digits) return '';
    return parseInt(digits, 10);
};

export type TFormData = {
    amount: number;
    turnover: string;
    purpose: NonNullable<
        NonNullable<
            NonNullable<PostApplicationRequestBody>['data']
        >['requested_products'][number]['purpose']
    >;
    duration?:
        'few_months' | 'ongoing' | 'one_to_two_years' | 'three_plus_years';
};

const mapStateToProps = (state: RootState) => {
    const flexiAccounts = _.filter(
        state.api.accounts?.data?.accounts,
        (account) => account.product_type !== 'merchant_cash_advance'
    );

    const ongoingFlexiLoan = _.find(
        flexiAccounts,
        (account) => account.loan_status === 'ongoing'
    );

    const currentPrincipal = ongoingFlexiLoan?.balance?.principal;

    return {
        createApplicationLoading:
            state?.applyFlexiLoanView?.createApplicationLoading!,
        documentRequirements: multiLoanselectors.getDocumentRequirements(state),
        isBankStatementsRequired:
            multiLoanselectors.isFlexiLoanBankStatementRequired(state),
        initialValues: {
            amount: undefined,
            purpose: undefined,
            turnover: selectors.getCompanyAnnualTurnover(state),
            duration: undefined
        },
        currentCreditLimit: ongoingFlexiLoan?.max_approved_loan_amount,
        currentPrincipal: currentPrincipal,
        formStep: state.applyFlexiLoanView.formStep,
        applicationId: state?.api?.application?.data?.application_id
    };
};

export type TDocumentRequirements = ReturnType<
    typeof multiLoanselectors.getDocumentRequirements
>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    createApplication: (values: TFormData) =>
        dispatch({
            type: CREATE_APPLICATION,
            values
        }),
    submitFlexiLoanApplication: () =>
        dispatch({
            type: APPLY_FLEXI_LOAN_SUBMIT_APPLICATION
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const ApplyFlexiLoanFormView = ({
    submitFlexiLoanApplication,
    isBankStatementsRequired,
    documentRequirements,
    createApplication,
    createApplicationLoading,
    currentCreditLimit,
    currentPrincipal,
    formStep,
    applicationId,
    initialValues
}: PropsFromRedux) => {
    const methods = useForm<TFormData>({
        defaultValues: initialValues,
        resolver: applyFlexiLoanFormResolver,
        mode: 'onSubmit',
        reValidateMode: 'onChange'
    });

    const {
        watch,
        control,
        register,
        formState: { errors }
    } = methods;
    const [docError, setDocError] = useState<string | null>(null);

    const formAmountValue = watch('amount');
    const watchedPurpose = watch('purpose');
    const watchedDuration = watch('duration');

    const onCreateApplicationValid = (values: TFormData) => {
        createApplication(values);
    };

    const handleSubmitApplication = () => {
        setDocError(null);
        if (isBankStatementsRequired) {
            setDocError('isMissingBankStatementRequired');
            return;
        }
        const missingDoc = documentRequirements?.find(
            ({ status, hasError }) => status === 'required' || hasError
        );
        if (missingDoc) {
            setDocError('isMissingDocumentRequirements');
            return;
        }
        submitFlexiLoanApplication();
    };

    return (
        <div>
            {formStep === 'createApplication' ? (
                <form onSubmit={methods.handleSubmit(onCreateApplicationValid)}>
                    <CreateApplicationStep
                        formAmountValue={formAmountValue}
                        currentPrincipal={currentPrincipal}
                        currentCreditLimit={currentCreditLimit}
                        createApplicationLoading={createApplicationLoading!}
                        control={control}
                        register={register}
                        errors={errors}
                        watchedPurpose={watchedPurpose}
                        watchedDuration={watchedDuration}
                    />
                </form>
            ) : (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmitApplication();
                    }}
                >
                    <UploadDocumentsStep
                        submitFailed={!!docError}
                        documentRequirements={documentRequirements}
                        applicationId={applicationId}
                    />
                </form>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ApplyFlexiLoanFormView);

const CreateApplicationStep = ({
    formAmountValue,
    currentPrincipal,
    currentCreditLimit,
    createApplicationLoading,
    control,
    register,
    errors,
    watchedPurpose,
    watchedDuration
}: {
    formAmountValue: number;
    currentPrincipal: number;
    currentCreditLimit: number;
    createApplicationLoading: boolean;
    control: ReturnType<typeof useForm<TFormData>>['control'];
    register: ReturnType<typeof useForm<TFormData>>['register'];
    errors: ReturnType<typeof useForm<TFormData>>['formState']['errors'];
    watchedPurpose: TFormData['purpose'];
    watchedDuration: TFormData['duration'];
}) => {
    const intl = useIntl();

    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormApplicationStepViewed();
        } else {
            tracking.ampli.newLoanApplicationStepViewed();
        }
    }, [isTopup]);

    return (
        <div>
            <NEW_IwLink
                className={styles.backBtn}
                to={{
                    pathname: '/'
                }}
            >
                <IconAngleLeft />
                <FormattedMessage
                    id="_.apply_flexi_loan.back_button"
                    defaultMessage="Go Back"
                />
            </NEW_IwLink>
            <Card className="items-stretch">
                <div className="flex flex-col p-l">
                    <h2 className="my-0 text-2xl font-med text-primary-10">
                        {isTopup ? (
                            <FormattedMessage
                                id="_.apply_topup_loan.title"
                                defaultMessage="Request more funds"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.reapply_loan.title"
                                defaultMessage="Apply for another loan"
                            />
                        )}
                    </h2>
                </div>

                <div className="p-l pt-0">
                    <Controller
                        control={control}
                        name="amount"
                        render={({ field: { onChange, value, name, ref } }) => (
                            <div className="my-l">
                                <Input.Root id={name}>
                                    <Input.Label>
                                        {isTopup ? (
                                            <FormattedMessage
                                                id="_.apply_flexi_loan.topupRequestedAmount.label"
                                                defaultMessage="How much more would you like to borrow?"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="_.apply_flexi_loan.requestedAmount.label"
                                                defaultMessage="How much do you need?"
                                            />
                                        )}
                                    </Input.Label>
                                    <Input.Control
                                        ref={ref}
                                        name={name}
                                        value={
                                            value
                                                ? intlFormatCurrency({
                                                      intl,
                                                      value
                                                  })
                                                : ''
                                        }
                                        onChange={(e) =>
                                            onChange(
                                                parseCurrencyInput(
                                                    e.target.value
                                                )
                                            )
                                        }
                                        placeholder={intl.formatMessage(
                                            {
                                                id: '_.apply_flexi_loan.requestedAmount.placeholder',
                                                defaultMessage:
                                                    'Enter an amount between {minAmount} and {maxAmount}'
                                            },
                                            {
                                                minAmount: intlFormatCurrency({
                                                    intl,
                                                    value: Constants.MIN_LOAN_AMOUNT
                                                }),
                                                maxAmount: intlFormatCurrency({
                                                    intl,
                                                    value: Constants.MAX_LOAN_AMOUNT
                                                })
                                            }
                                        )}
                                    />
                                    {errors.amount && (
                                        <Input.Status>
                                            <ErrorMessage.Root>
                                                <ErrorMessage.Icon />
                                                <ErrorMessage.Text data-testid="formErrors">
                                                    {errors.amount.message}
                                                </ErrorMessage.Text>
                                            </ErrorMessage.Root>
                                        </Input.Status>
                                    )}
                                </Input.Root>
                            </div>
                        )}
                    />

                    {isTopup &&
                        !!formAmountValue &&
                        formAmountValue <=
                            Constants.MAX_LOAN_AMOUNT - currentPrincipal &&
                        (formAmountValue + currentPrincipal >
                        currentCreditLimit ? (
                            <p className={styles.newCreditLimit}>
                                <FormattedMessage
                                    id="_.apply_flexi_loan.newCreditLimit"
                                    defaultMessage="Your new credit limit would be:"
                                />{' '}
                                <strong>
                                    {intlFormatCurrency({
                                        intl,
                                        value:
                                            formAmountValue + currentPrincipal
                                    })}
                                </strong>
                            </p>
                        ) : (
                            <p className={styles.newCreditLimit}>
                                <FormattedMessage
                                    id="_.apply_flexi_loan.unchangedCreditLimit"
                                    defaultMessage="Your credit limit will remain:"
                                />{' '}
                                <strong>
                                    {intlFormatCurrency({
                                        intl,
                                        value: currentCreditLimit
                                    })}
                                </strong>
                            </p>
                        ))}

                    <div className="my-l">
                        <Select
                            {...register('purpose')}
                            id="purpose"
                            defaultValue=""
                            label={
                                isTopup ? (
                                    <FormattedMessage
                                        id="_.apply_flexi_loan.topupLoanPurpose.label"
                                        defaultMessage="What is your main reason for requesting more funds?"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="_.apply_flexi_loan.loanPurpose.label"
                                        defaultMessage="What is your main reason for requesting a loan?"
                                    />
                                )
                            }
                            className={
                                !watchedPurpose
                                    ? 'font-reg text-structure-60'
                                    : ''
                            }
                        >
                            <option disabled hidden value="">
                                {intl.formatMessage({
                                    id: '_.apply_flexi_loan.loanPurpose.placeholder',
                                    defaultMessage: 'Please choose an option'
                                })}
                            </option>
                            {(getCountry() === 'DE'
                                ? loanPurposeOptionsDE
                                : loanPurposeOptions
                            ).map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                        {errors.purpose && (
                            <ErrorMessage.Root className="mt-s">
                                <ErrorMessage.Icon />
                                <ErrorMessage.Text data-testid="formErrors">
                                    {errors.purpose.message}
                                </ErrorMessage.Text>
                            </ErrorMessage.Root>
                        )}
                    </div>

                    <Controller
                        control={control}
                        name="turnover"
                        render={({ field: { onChange, value, name, ref } }) => (
                            <div className="my-l">
                                <Input.Root id={name}>
                                    <Input.Label>
                                        <FormattedMessage
                                            id="_.apply_flexi_loan.turnover.label"
                                            defaultMessage="What was your last 12 months turnover?"
                                        />
                                    </Input.Label>
                                    <Input.Control
                                        ref={ref}
                                        name={name}
                                        value={
                                            value
                                                ? intlFormatCurrency({
                                                      intl,
                                                      value: Number(value)
                                                  })
                                                : ''
                                        }
                                        onChange={(e) =>
                                            onChange(
                                                parseCurrencyInput(
                                                    e.target.value
                                                )
                                            )
                                        }
                                        placeholder={`${intl.formatMessage({
                                            id: '_.apply_flexi_loan.turnover.placeholder',
                                            defaultMessage:
                                                'Enter turnover amount'
                                        })} - ${intl.formatNumber(100000)}`}
                                    />
                                    {errors.turnover && (
                                        <Input.Status>
                                            <ErrorMessage.Root>
                                                <ErrorMessage.Icon />
                                                <ErrorMessage.Text data-testid="formErrors">
                                                    {errors.turnover.message}
                                                </ErrorMessage.Text>
                                            </ErrorMessage.Root>
                                        </Input.Status>
                                    )}
                                </Input.Root>
                            </div>
                        )}
                    />

                    {getCountry() === 'DE' && (
                        <div className="my-l">
                            <Select
                                {...register('duration')}
                                id="duration"
                                defaultValue=""
                                label={
                                    <FormattedMessage
                                        id="_.apply_flexi_loan.loanDuration.label"
                                        defaultMessage="Wie lange benötigen Sie den Kredit?"
                                    />
                                }
                                className={
                                    !watchedDuration
                                        ? 'font-reg text-structure-60'
                                        : ''
                                }
                            >
                                <option disabled hidden value="">
                                    {intl.formatMessage({
                                        id: '_.apply_flexi_loan.loanDuration.placeholder',
                                        defaultMessage:
                                            'Bitte wählen Sie eine Option aus'
                                    })}
                                </option>
                                {loanDurationOptionsDE.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                            {errors.duration && (
                                <ErrorMessage.Root className="mt-s">
                                    <ErrorMessage.Icon />
                                    <ErrorMessage.Text data-testid="formErrors">
                                        {errors.duration.message}
                                    </ErrorMessage.Text>
                                </ErrorMessage.Root>
                            )}
                        </div>
                    )}

                    <Button
                        className="mx-auto mt-2xl min-w-[150px]"
                        loading={createApplicationLoading}
                        type="submit"
                    >
                        <FormattedMessage
                            id="_.apply_flexi_loan.nextCTA"
                            defaultMessage="Next"
                        />
                    </Button>
                </div>
            </Card>
        </div>
    );
};

const UploadDocumentsStep = ({
    submitFailed,
    documentRequirements,
    applicationId
}: {
    submitFailed: boolean;
    applicationId: string;
    documentRequirements: TDocumentRequirements;
}) => {
    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormDocumentsStepViewed({
                application_id: applicationId
            });
        } else {
            tracking.ampli.newLoanDocumentsStepViewed({
                application_id: applicationId
            });
        }
    }, [applicationId, isTopup]);

    return (
        <ApprovalRequirements
            submitFailed={submitFailed}
            documentRequirements={documentRequirements}
        />
    );
};
